import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Panel, TactLoader } from "../components/Tactical";
import ReliabilityBadge from "../components/ReliabilityBadge";
import { toast } from "sonner";
import { Shield, Flag, Search } from "lucide-react";

const FILTERS = [
  { id: "all", label: "All", color: "bg-[#7C3AED]" },
  { id: "below50", label: "Below 50", color: "bg-[#E11D48]" },
  { id: "above50", label: "50 and above", color: "bg-[#00FF87]" },
  { id: "flagged", label: "Flagged", color: "bg-[#FFCC00]" },
];

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString([], { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

export default function ReliabilityTab() {
  const [filter, setFilter] = useState("below50");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/reliability?filter=${filter}`);
      setRows(data.rows || []);
    } catch (e) {
      toast.error(`Reliability list failed: ${formatApiError(e)}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        (r.username || "").toLowerCase().includes(q) ||
        (r.game_id || "").toLowerCase().includes(q)
    );
  }, [rows, query]);

  return (
    <div className="space-y-6" data-testid="admin-reliability-tab">
      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#7C3AED] mb-1">
          // player trust index
        </div>
        <h2 className="font-heading text-3xl uppercase tracking-wider">Reliability</h2>
        <p className="font-mono text-[10px] text-[#71717A] mt-1 leading-relaxed max-w-2xl">
          0–100 score per player. Starts at 70. Rewards match completion, on-time submissions,
          chat engagement, and score-agreement. Penalises reports + no-show forfeits.
          Players with <span className="text-[#E11D48]">&lt; 50 AND ≥ 5 matches</span> are auto-flagged for review (not banned).
        </p>
      </div>

      {/* Filter toolbar */}
      <div className="flex items-center gap-3 flex-wrap" data-testid="reliability-toolbar">
        <div className="flex gap-1 border border-[#E4E4E7] dark:border-[#2A2A30] p-1">
          {FILTERS.map((f) => {
            const active = filter === f.id;
            return (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                data-testid={`reliability-filter-${f.id}`}
                className={`px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] transition-colors ${
                  active
                    ? `${f.color} text-black`
                    : "text-[#71717A] hover:text-[#09090B] dark:hover:text-[#F4F4F5]"
                }`}
              >
                {f.label}
              </button>
            );
          })}
        </div>
        <div className="flex-1 min-w-[200px] relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#71717A]" aria-hidden />
          <input
            data-testid="reliability-search-input"
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search username or game ID…"
            className="w-full bg-[#FAFAFC] dark:bg-[#09090B] border border-[#E4E4E7] dark:border-[#2A2A30] pl-9 pr-3 py-2 font-mono text-xs text-[#09090B] dark:text-[#F4F4F5] focus:outline-none focus:border-[#7C3AED]"
          />
        </div>
        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A]" data-testid="reliability-count">
          {visible.length} / {rows.length}
        </span>
      </div>

      {loading ? (
        <TactLoader label="LOADING" />
      ) : visible.length === 0 ? (
        <Panel className="font-mono text-[#71717A] text-sm" testId="reliability-empty">
          [ NO PLAYERS MATCH THIS FILTER. ]
        </Panel>
      ) : (
        <div className="bg-[#FFFFFF] dark:bg-[#1A1A1E] border border-[#E4E4E7] dark:border-[#2A2A30] overflow-x-auto" data-testid="reliability-list">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#E4E4E7] dark:border-[#2A2A30]">
                <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A]">PLAYER</th>
                <th className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A]">SCORE</th>
                <th className="text-right py-3 px-4 font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A]">MATCHES</th>
                <th className="text-right py-3 px-4 font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A]">COMPLETION</th>
                <th className="text-right py-3 px-4 font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A]">ON TIME</th>
                <th className="text-right py-3 px-4 font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A]">CHAT</th>
                <th className="text-right py-3 px-4 font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A]">REPORTS</th>
                <th className="text-right py-3 px-4 font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A]">FORFEITS</th>
                <th className="text-right py-3 px-4 font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A]">UPDATED</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((r) => {
                const b = r.breakdown || {};
                const pct = (n) => `${Math.round((n || 0) * 100)}%`;
                return (
                  <tr
                    key={r.user_id}
                    className={`border-b border-[#E4E4E7] dark:border-[#2A2A30] hover:bg-[#7C3AED]/5 ${r.flagged ? "border-l-2 border-l-[#E11D48]" : ""}`}
                    data-testid={`reliability-row-${r.user_id}`}
                  >
                    <td className="py-3 px-4">
                      <Link
                        to={`/players/${encodeURIComponent(r.username)}`}
                        className="inline-flex items-center gap-2 font-heading uppercase text-[#09090B] dark:text-[#F4F4F5] hover:text-[#7C3AED]"
                      >
                        {r.flagged && <Flag className="w-3 h-3 text-[#E11D48]" aria-hidden />}
                        {r.username}
                      </Link>
                      <div className="font-mono text-[9px] text-[#71717A] tracking-[0.2em] mt-0.5">
                        {r.platform?.toUpperCase() || "MOBILE"} · {r.game_id}{r.banned ? " · BANNED" : ""}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <ReliabilityBadge score={r.score} flagged={r.flagged} size="md" testId={`reliability-badge-${r.user_id}`} />
                    </td>
                    <td className="py-3 px-4 text-right font-mono">{b.matches_in_window ?? 0}</td>
                    <td className="py-3 px-4 text-right font-mono">{pct(b.completion_rate)}</td>
                    <td className="py-3 px-4 text-right font-mono">{pct(b.on_time_rate)}</td>
                    <td className="py-3 px-4 text-right font-mono">{pct(b.chat_engagement_rate)}</td>
                    <td className={`py-3 px-4 text-right font-mono ${b.unique_reports ? "text-[#E11D48]" : ""}`}>{b.unique_reports ?? 0}</td>
                    <td className={`py-3 px-4 text-right font-mono ${b.auto_forfeits ? "text-[#EA580C]" : ""}`}>{b.auto_forfeits ?? 0}</td>
                    <td className="py-3 px-4 text-right font-mono text-[10px] text-[#71717A]">{fmt(r.updated_at)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
